import logger from '../log';
import { getUTMParams, type UTMParams } from './index';

const STORAGE_KEY = 'storedUTMParams';
const EXPIRY_DAYS = 1;

interface StoredUTMData {
  utmParams: UTMParams;
  timestamp: number;
}

const SEARCH_ENGINE_SOURCES: Record<string, string> = {
  'google.': 'google',
  'bing.': 'bing',
  'yahoo.': 'yahoo',
};

function stripWwwPrefix(hostname: string): string {
  return hostname.replace(/^www\./, '').toLowerCase();
}

function getDerivedUTMParamsFromReferrer(): UTMParams {
  const referrer = document.referrer;
  if (!referrer) {
    return {
      utm_source: 'direct',
      utm_medium: 'none',
    };
  }

  try {
    const referrerHostname = stripWwwPrefix(new URL(referrer).hostname);
    const currentHostname = stripWwwPrefix(window.location.hostname);

    if (!referrerHostname || referrerHostname === currentHostname) {
      return {
        utm_source: 'direct',
        utm_medium: 'none',
      };
    }

    for (const [hostPattern, source] of Object.entries(SEARCH_ENGINE_SOURCES)) {
      if (referrerHostname.includes(hostPattern)) {
        return {
          utm_source: source,
          utm_medium: 'organic',
        };
      }
    }

    return {
      utm_source: referrerHostname,
      utm_medium: 'referral',
    };
  } catch {
    return {
      utm_source: 'direct',
      utm_medium: 'none',
    };
  }
}

/**
 * Captures UTM parameters from the current URL and stores them (last-touch attribution).
 * Always overwrites existing UTMs with the latest from the URL.
 */
export function captureAndStoreUTMParams(): void {
  if (typeof window === 'undefined') return;

  try {
    const currentUTMParams = getUTMParams();
    const hasCurrentUTMParams = currentUTMParams && Object.keys(currentUTMParams).length > 0;

    // Last-touch attribution for explicit campaign UTMs.
    if (hasCurrentUTMParams) {
      const storedData: StoredUTMData = {
        utmParams: currentUTMParams,
        timestamp: Date.now(),
      };

      localStorage.setItem(STORAGE_KEY, JSON.stringify(storedData));
      return;
    }

    // Keep previously stored attribution for this session window.
    const hasStoredUTMParams = getStoredUTMParams();
    if (hasStoredUTMParams) {
      return;
    }

    // Ensure every session has a source/medium (direct, organic, referral).
    const fallbackUTMParams = getDerivedUTMParamsFromReferrer();
    const storedData: StoredUTMData = {
      utmParams: fallbackUTMParams,
      timestamp: Date.now(),
    };

    localStorage.setItem(STORAGE_KEY, JSON.stringify(storedData));
  } catch (error) {
    logger.error('Failed to capture and store UTM parameters:', error instanceof Error ? error.message : String(error));
  }
}

/**
 * Retrieves stored UTM parameters from localStorage.
 * Returns null if expired, not found, or on server-side.
 */
export function getStoredUTMParams(): UTMParams | null {
  if (typeof window === 'undefined') return null;

  try {
    const storedValue = localStorage.getItem(STORAGE_KEY);
    if (!storedValue) {
      return null;
    }

    const data: StoredUTMData = JSON.parse(storedValue);
    const expiryInMs = EXPIRY_DAYS * 24 * 60 * 60 * 1000;

    // Check if expired
    if (Date.now() - data.timestamp > expiryInMs) {
      clearStoredUTMParams();
      return null;
    }

    return data.utmParams;
  } catch (error) {
    logger.error(
      'Failed to load stored UTM parameters from localStorage:',
      error instanceof Error ? error.message : String(error),
    );
    return null;
  }
}

/**
 * Clears stored UTM parameters from localStorage.
 * Called after form submission to reset attribution for next conversion.
 */
export function clearStoredUTMParams(): void {
  if (typeof window === 'undefined') return;

  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch (error) {
    logger.error(
      'Failed to clear stored UTM parameters from localStorage:',
      error instanceof Error ? error.message : String(error),
    );
  }
}
